import { Button, Group, Modal, Table, Tabs, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import Image from 'next/image'
import { useState } from 'react'

import * as DisplayData from '@/components/commons/DisplayData'
import { PaymentMethodOption as PaymentMethodOptionType } from '@/types'

interface Props {
  paymentMethods: any
  availablePaymentMethods: any
}

export default function Conditions({
  paymentMethods,
  availablePaymentMethods
}: Props) {
  // Hooks
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // States
  const [currentTab, setCurrentTab] = useState<string>(paymentMethods[0] || '')
  const [modalOtherPaymentsOpen, setModalOtherPaymentsOpen] = useState<boolean>(false)

  // Actions
  const handleTabChange = (value: any) => {
    setCurrentTab(value)
  }

  return (
    <>
      <Button onClick={() => setModalOtherPaymentsOpen(true)} variant="subtle" mt="xs">
        Ver condições de pagamento
      </Button>
      <Modal
        centered
        size="xl"
        overflow="inside"
        title={<Title order={isXs ? 6 : 4}>Condições de pagamento</Title>}
        opened={modalOtherPaymentsOpen}
        onClose={() => setModalOtherPaymentsOpen(false)}>
        <Tabs
          color="custom"
          defaultValue={paymentMethods[0]}
          value={currentTab}
          onTabChange={value => handleTabChange(value)}>
          <Tabs.List mb="sm">
            {paymentMethods?.map((paymentMethod: string) => (
              <Tabs.Tab key={paymentMethod} value={paymentMethod}>
                <DisplayData.PaymentMethodTitle paymentMethodCode={paymentMethod} />
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {paymentMethods?.map((paymentMethod: string, index: number) => (
            <Tabs.Panel key={paymentMethod} value={paymentMethod}>
              {paymentMethod === 'creditCard' ? (
                <Table highlightOnHover>
                  <thead>
                    <tr>
                      <th colSpan={4}>
                        <Group spacing="xs" position="center" mb="xs">
                          {availablePaymentMethods?.creditCard?.map(
                            (option: PaymentMethodOptionType) => (
                              <Image
                                key={option.paymentMethod?.code}
                                alt={option.paymentMethod?.name}
                                width={40}
                                height={40}
                                src={option.paymentMethod?.brand?.logo}
                                style={{ borderRadius: '3px' }}
                              />
                            )
                          )}
                        </Group>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(availablePaymentMethods?.creditCard[0]?.installments) &&
                      availablePaymentMethods?.creditCard[0]?.installments?.map(
                        (installment: any, index: number) => (
                          <tr key={paymentMethod + index}>
                            <td>{installment.installments}x</td>
                            <td>
                              de <DisplayData.CurrencyField value={installment.installmentAmount} />
                            </td>
                            <td>
                              {installment.interestRate > 0 ? (
                                <>
                                  com juros de{' '}
                                  <DisplayData.PercentageField value={installment.interestRate} />{' '}
                                  a.m.
                                </>
                              ) : (
                                'sem juros'
                              )}
                            </td>
                            <td>
                              total <DisplayData.CurrencyField value={installment.total} />
                              {installment.discountAmount > 0 ? (
                                <Text size="xs" color="green">
                                  desconto{' '}
                                  <DisplayData.CurrencyField value={installment.discountAmount} />
                                </Text>
                              ) : (
                                <></>
                              )}
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                </Table>
              ) : (
                <>
                  <Text key={index} weight="bold" mb="xs">
                    <DisplayData.CurrencyField
                      value={availablePaymentMethods?.[paymentMethod][0].total}
                    />{' '}
                    no <DisplayData.PaymentMethodTitle paymentMethodCode={paymentMethod} />
                    {availablePaymentMethods?.[paymentMethod][0].discountAmount > 0 ? (
                      <Text size="xs" color="green">
                        desconto{' '}
                        <DisplayData.CurrencyField
                          value={availablePaymentMethods?.[paymentMethod][0].discountAmount}
                        />
                      </Text>
                    ) : (
                      <></>
                    )}
                  </Text>
                  <Text color="dimmed">
                    {paymentMethod === 'pix'
                      ? 'O pagamento é instantâneo e só pode ser à vista. Após finalizar a compra, basta realizar o pagamento utilizando o QR Code ou utilizando o código Copia e Cola.'
                      : ''}
                    {paymentMethod === 'billingNote'
                      ? 'O boleto será gerado após a finalização de sua compra. Imprima e pague no banco ou pague pela internet utilizando o código de barras do boleto.'
                      : ''}
                    {paymentMethod === 'installmentBillingNote' ? (
                      <>
                        <Text color="dimmed" mb="xs">
                          A opção de financiamento através de boleto parcelado é oferecido pela Koin
                          e está sujeito a cobrança de juros.
                        </Text>
                        <Image
                          src="/images/checkout/installment_billingnote_koin.png"
                          alt="Boleto parcelado Koin"
                          layout="responsive"
                          width={1068}
                          height={760}
                          quality={60}
                        />
                      </>
                    ) : (
                      ''
                    )}
                  </Text>
                </>
              )}
            </Tabs.Panel>
          ))}
        </Tabs>
      </Modal>
    </>
  )
}
